import React from 'react';
import { FixedSizeList as List } from 'react-window';
import AjaxSpinner from '@/components/AjaxSpinner';
import { TSavedQueryPreset } from '@/features/dashboard/components/DashboardFilterModal/components/PresetChoices/types';
import { getSavedQueryPresets } from '@/features/saved-query-dashboard-filter-presets/apiSlice';
import {
  Banner,
  DropdownCheckboxItem,
  SearchField,
} from '@trevor/ui-components';
import DashboardFilterRef from '@/util/DashboardFilterRef';
import styled from 'styled-components';

const NoResultsFound = styled.li`
  padding: var(--spacing-s) var(--spacing-m);
`;

const ErrorBanner = styled(Banner).attrs({
  severity: 'error',
  children:
    'Problem loading filter choices, please try again in a few minutes.',
})`
  width: 170px;
`;

type TPreset = string;
type TPresetUpdateFn = F3<DashboardFilterRef, TPreset, boolean>;

export const DashboardFilterSavedQueryPresets: React.FC<{
  filter: DashboardFilterRef;
  savedQueryPresets: TSavedQueryPreset;
  selectedValues: TPreset[];
  onUpdate: TPresetUpdateFn;
}> = ({ filter, savedQueryPresets, selectedValues, onUpdate }) => {
  const { data, isFetching, isError } = getSavedQueryPresets(savedQueryPresets);
  const [searchTerm, setSearchTerm] = React.useState('');

  if (isFetching) return <AjaxSpinner small />;

  const presets =
    data?.filter((preset) =>
      searchTerm ? preset.includes(searchTerm) : true,
    ) ?? [];

  const showSearchField = (data?.length ?? 0) > 10;
  const useVirtualization = (data?.length ?? 0) > 200;
  const noSearchResults = !!searchTerm && presets.length === 0;

  const ListComponent = useVirtualization
    ? VirtualizedPresetsList
    : PresetsList;

  return (
    <>
      {showSearchField && <SearchField onChange={setSearchTerm} />}
      {noSearchResults && <NoResultsFound>No results found</NoResultsFound>}
      {isError && <ErrorBanner />}
      <ListComponent
        filter={filter}
        presets={presets}
        selectedValues={selectedValues}
        onUpdate={onUpdate}
      />
    </>
  );
};

type PresetListProps = {
  filter: DashboardFilterRef;
  presets: TPreset[];
  selectedValues: TPreset[];
  onUpdate: TPresetUpdateFn;
};

const PresetsList: React.FC<PresetListProps> = ({
  filter,
  presets,
  selectedValues,
  onUpdate,
}) => {
  return (
    <>
      {presets.map((preset) => (
        <DropdownCheckboxItem
          key={preset}
          selected={selectedValues.includes(preset)}
          label={preset === '' ? 'EMPTY' : preset}
          onClick={() =>
            onUpdate(filter, preset, !selectedValues.includes(preset))
          }
        />
      ))}
    </>
  );
};

const VirtualizedPresetsList: React.FC<PresetListProps> = ({
  filter,
  presets,
  selectedValues,
  onUpdate,
}) => {
  const height = Math.min(500, 40 * presets.length);

  return (
    <List itemCount={presets.length} height={height} width={360} itemSize={40}>
      {({ index, style }) => (
        <div style={style}>
          <DropdownCheckboxItem
            selected={selectedValues.includes(presets[index])}
            label={presets[index] === '' ? 'EMPTY' : presets[index]}
            onClick={() =>
              onUpdate(
                filter,
                presets[index],
                !selectedValues.includes(presets[index]),
              )
            }
          />
        </div>
      )}
    </List>
  );
};
